import { useState, useEffect, useRef } from 'react';
import * as Y from 'yjs';
import { io } from 'socket.io-client';
import Collaboration from '@tiptap/extension-collaboration';
import { CustomCollaborationCursor } from '../extensions/CustomCollaborationCursor';
import { SocketIOProvider } from '../providers/SocketIOProvider';
import { backendUrl } from '../../../../config';
import { backendClient } from '../../../../api/backend';

/**
 * useCollaboration — manages the Socket.IO connection, Yjs document, and
 * collaboration TipTap extensions for a given contract.
 *
 * Returns stable refs (socketRef, providerRef, ydocRef) plus reactive state
 * (status, activeUsers, socketState, collaborationExtensions) that can be
 * passed into useEditor and other hooks.
 */
export function useCollaboration({ contractId, currentUser, currentUserData, saveBeforeLeaveRef }) {
  const [status, setStatus] = useState('connecting');
  const [activeUsers, setActiveUsers] = useState([]);
  // socketState mirrors socketRef but is a React state value so hooks that
  // consume it re-render when the socket is (re)created.
  const [socketState, setSocketState] = useState(null);
  const [collaborationExtensions, setCollaborationExtensions] = useState([]);

  const socketRef = useRef(null);
  const providerRef = useRef(null);
  const ydocRef = useRef(null);

  useEffect(() => {
    if (!contractId || !currentUser) return;

    // Tear down any previous connection before creating a new one.
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    if (providerRef.current) {
      providerRef.current.destroy();
      providerRef.current = null;
    }
    if (ydocRef.current) {
      ydocRef.current.destroy();
      ydocRef.current = null;
    }

    // Yjs document
    const ydoc = new Y.Doc();
    ydocRef.current = ydoc;

    // Auth token
    const token = backendClient.getToken();
    if (!token?.access_token) {
      console.error('useCollaboration: missing access token');
      return;
    }

    // Socket
    const socketProtocol = window.location.protocol === 'https:' ? 'https://' : 'http://';
    const backendUrlWithoutProtocol = backendUrl.replace(/^https?:\/\//, '');
    const socketUrl = `${socketProtocol}${backendUrlWithoutProtocol}`;

    const socket = io(socketUrl, {
      path: '/contract/editor/socket.io',
      auth: {
        token: token.access_token,
        contract_id: contractId,
        color: currentUserData.color,
      },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      transports: ['websocket', 'polling'],
    });

    socketRef.current = socket;
    setSocketState(socket);

    // Provider
    const provider = new SocketIOProvider(socket, contractId, ydoc);
    providerRef.current = provider;

    provider.on('status', ({ status: s }) => {
      setStatus(s === 'connected' ? 'connected' : 'disconnected');
    });

    // Active users list
    socket.on('user_status', (data) => {
      if (!data || !Array.isArray(data.users)) return;
      setActiveUsers(data.users);

      // Remove stale awareness cursors
      if (providerRef.current?.awareness) {
        const currentUserIds = new Set(data.users.map(u => u.id));
        const staleClientIds = [];
        providerRef.current.awareness.getStates().forEach((state, clientId) => {
          if (
            clientId !== providerRef.current.doc.clientID &&
            state.user?.id &&
            !currentUserIds.has(state.user.id)
          ) {
            staleClientIds.push(clientId);
          }
        });
        if (staleClientIds.length > 0) {
          providerRef.current.removeAwarenessStates(staleClientIds, null);
        }
      }
    });

    // Set initial user state in awareness
    const user = {
      name: `${currentUserData.first_name || ''} ${currentUserData.last_name || ''}`.trim() || 'User',
      color: currentUserData.color,
      id: String(currentUser.id),
    };
    provider.awareness.setLocalState({ user, selection: null });

    // Build TipTap collaboration extensions
    const collabExtensions = [
      Collaboration.configure({ document: ydoc, field: 'content' }),
      CustomCollaborationCursor.configure({ provider, user }),
    ];
    setCollaborationExtensions(collabExtensions);

    return () => {
      // Save before disconnecting so the emit goes through on the live socket
      saveBeforeLeaveRef?.current?.(socket);
      socket.disconnect();
      provider.destroy();
      ydoc.destroy();
    };
  }, [contractId, currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    status,
    activeUsers,
    socketState,
    socketRef,
    providerRef,
    ydocRef,
    collaborationExtensions,
  };
}
